import React, { useState, useEffect } from 'react';
import { fetchData, patchData } from '../../services/api';
import { ApiURLs } from '../../constant/ApiUrls';

const REQUIRED_FIELDS = [
  { key: 'company_name', label: 'Company Name' },
  { key: 'contact_name', label: 'Contact Name' },
  { key: 'contact_email', label: 'Email' },
  { key: 'contact_phone', label: 'Phone' },
];

const IMPORTANT_FIELDS = [
  { key: 'address', label: 'Address' },
  { key: 'payment_iban', label: 'IBAN' },
  { key: 'payment_bic', label: 'BIC' },
  { key: 'billing_contact_name', label: 'Billing Contact' },
  { key: 'billing_contact_email', label: 'Invoice Email' },
];

const STATUS_COLORS = {
  pending: { bg: '#fff3e0', color: '#e65100', label: 'Pending' },
  approved: { bg: '#e8f5e9', color: '#2e7d32', label: 'Approved' },
  rejected: { bg: '#ffebee', color: '#c62828', label: 'Rejected' },
};

const PendingDealers = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      const res = await fetchData(ApiURLs.DEALER_APPLICATIONS);
      if (res?.data) {
        setApplications(res.data);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    }
    setLoading(false);
  };

  const updateStatus = async (id, status) => {
    try {
      await patchData(`${ApiURLs.DEALER_APPLICATIONS}/${id}`, { status });
      setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
    } catch (err) {
      console.error('Failed to update:', err);
    }
  };

  const getMissingFields = (app) => {
    return IMPORTANT_FIELDS.filter(f => !app[f.key] || app[f.key].trim() === '');
  };

  const filtered = filter === 'all' ? applications : applications.filter(a => a.status === filter);

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const parseJSON = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
  };

  if (loading) {
    return <div style={styles.container}><p>Loading applications...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Dealer Applications</h2>
        <div style={styles.filterRow}>
          {['all', 'pending', 'approved', 'rejected'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ ...styles.filterBtn, ...(filter === f ? styles.filterActive : {}) }}>
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && <span style={styles.badge}>{applications.filter(a => f === 'all' || a.status === f).length}</span>}
              {f === 'all' && <span style={styles.badge}>{applications.length}</span>}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={styles.empty}>No applications found.</div>
      ) : (
        filtered.map(app => {
          const missing = getMissingFields(app);
          const expanded = expandedId === app.id;
          const statusInfo = STATUS_COLORS[app.status] || STATUS_COLORS.pending;
          const socialLinks = parseJSON(app.social_links);
          const openingHours = parseJSON(app.opening_hours);
          const staffUsers = parseJSON(app.staff_users);

          return (
            <div key={app.id} style={styles.card}>
              <div style={styles.cardHeader} onClick={() => setExpandedId(expanded ? null : app.id)}>
                <div style={styles.cardLeft}>
                  <div style={styles.companyRow}>
                    <h3 style={styles.companyName}>{app.company_name}</h3>
                    <span style={{ ...styles.statusBadge, background: statusInfo.bg, color: statusInfo.color }}>
                      {statusInfo.label}
                    </span>
                    {missing.length > 0 && app.status === 'pending' && (
                      <span style={styles.warningBadge} title={`Missing: ${missing.map(m => m.label).join(', ')}`}>
                        {missing.length} missing
                      </span>
                    )}
                  </div>
                  <p style={styles.contactInfo}>
                    {app.contact_name} &middot; {app.contact_email} &middot; {app.country_code} {app.contact_phone}
                  </p>
                  <p style={styles.dateText}>Applied: {formatDate(app.created_at)}</p>
                </div>
                <div style={styles.cardRight}>
                  <span style={styles.expandIcon}>{expanded ? '▲' : '▼'}</span>
                </div>
              </div>

              {expanded && (
                <div style={styles.details}>
                  <div style={styles.detailGrid}>
                    <div style={styles.detailSection}>
                      <h4 style={styles.sectionTitle}>Company Details</h4>
                      <DetailRow label="Business Name" value={app.company_name} />
                      <DetailRow label="Registration No." value={app.registration_number} />
                      <DetailRow label="VAT Number" value={app.vat_number} />
                      <DetailRow label="Address" value={app.address} warn={!app.address} />
                      <DetailRow label="Website" value={app.website} />
                    </div>

                    <div style={styles.detailSection}>
                      <h4 style={styles.sectionTitle}>Contact</h4>
                      <DetailRow label="Name" value={app.contact_name} />
                      <DetailRow label="Email" value={app.contact_email} />
                      <DetailRow label="Phone" value={`${app.country_code} ${app.contact_phone}`} />
                    </div>

                    <div style={styles.detailSection}>
                      <h4 style={styles.sectionTitle}>Billing & Payments</h4>
                      <DetailRow label="Billing Contact" value={app.billing_contact_name} warn={!app.billing_contact_name} />
                      <DetailRow label="Invoice Email" value={app.billing_contact_email} warn={!app.billing_contact_email} />
                      <DetailRow label="IBAN" value={app.payment_iban} warn={!app.payment_iban} />
                      <DetailRow label="BIC" value={app.payment_bic} warn={!app.payment_bic} />
                    </div>

                    <div style={styles.detailSection}>
                      <h4 style={styles.sectionTitle}>Account</h4>
                      <DetailRow label="Username" value={app.preferred_username} />
                      <DetailRow label="Subscription" value={app.subscription_plan} />
                      <DetailRow label="Go-Live Date" value={app.preferred_go_live_date} />
                      <DetailRow label="Categories" value={app.categories} />
                      <DetailRow label="API/Stock" value={app.api_requirements} />
                      <DetailRow label="Approx Listings" value={app.approx_listings} />
                    </div>
                  </div>

                  {app.dealer_profile_bio && (
                    <div style={styles.bioSection}>
                      <h4 style={styles.sectionTitle}>Dealer Profile</h4>
                      <p style={styles.bioText}>{app.dealer_profile_bio}</p>
                    </div>
                  )}

                  {socialLinks && Object.values(socialLinks).some(v => v) && (
                    <div style={styles.bioSection}>
                      <h4 style={styles.sectionTitle}>Social Links</h4>
                      {Object.entries(socialLinks).map(([k, v]) => v ? <DetailRow key={k} label={k} value={v} /> : null)}
                    </div>
                  )}

                  {staffUsers && staffUsers.length > 0 && staffUsers.some(s => s.name) && (
                    <div style={styles.bioSection}>
                      <h4 style={styles.sectionTitle}>Staff Users</h4>
                      {staffUsers.filter(s => s.name).map((s, i) => (
                        <p key={i} style={styles.staffRow}>{s.name} - {s.email} {s.phone && `- ${s.phone}`}</p>
                      ))}
                    </div>
                  )}

                  {app.notes && (
                    <div style={styles.bioSection}>
                      <h4 style={styles.sectionTitle}>Notes</h4>
                      <p style={styles.bioText}>{app.notes}</p>
                    </div>
                  )}

                  {app.logo && (
                    <div style={styles.bioSection}>
                      <h4 style={styles.sectionTitle}>Branding</h4>
                      <div style={{ display: 'flex', gap: 16 }}>
                        {app.logo && <img src={app.logo} alt="Logo" style={{ height: 60, borderRadius: 8 }} />}
                        {app.dealer_banner && <img src={app.dealer_banner} alt="Banner" style={{ height: 60, borderRadius: 8 }} />}
                      </div>
                    </div>
                  )}

                  <div style={styles.actionRow}>
                    {app.status !== 'approved' && (
                      <button style={styles.approveBtn} onClick={() => updateStatus(app.id, 'approved')}>
                        Approve
                      </button>
                    )}
                    {app.status !== 'rejected' && (
                      <button style={styles.rejectBtn} onClick={() => updateStatus(app.id, 'rejected')}>
                        Reject
                      </button>
                    )}
                    {app.status !== 'pending' && (
                      <button style={styles.pendingBtn} onClick={() => updateStatus(app.id, 'pending')}>
                        Set Pending
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const DetailRow = ({ label, value, warn }) => (
  <div style={styles.detailRow}>
    <span style={styles.detailLabel}>{label}</span>
    <span style={{ ...styles.detailValue, ...(warn ? styles.warnValue : {}) }}>
      {value || (warn ? 'Missing' : '-')}
      {warn && <span style={styles.redDot} />}
    </span>
  </div>
);

const styles = {
  container: { padding: '24px', maxWidth: 1200 },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: '#1a1a2e', marginBottom: 16 },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  filterBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1.5px solid #e0e0e0',
    background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
    color: '#666', display: 'flex', alignItems: 'center', gap: 6,
  },
  filterActive: { background: '#0864f7', color: '#fff', borderColor: '#0864f7' },
  badge: {
    background: 'rgba(0,0,0,0.1)', borderRadius: 10, padding: '2px 8px',
    fontSize: 12, fontWeight: 700, minWidth: 20, textAlign: 'center',
  },
  empty: { padding: 40, textAlign: 'center', color: '#999', fontSize: 16 },
  card: {
    background: '#fff', borderRadius: 12, border: '1px solid #e8e8e8',
    marginBottom: 12, overflow: 'hidden',
  },
  cardHeader: {
    padding: '16px 20px', cursor: 'pointer', display: 'flex',
    justifyContent: 'space-between', alignItems: 'flex-start',
  },
  cardLeft: { flex: 1 },
  cardRight: { paddingLeft: 12 },
  companyRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 },
  companyName: { fontSize: 17, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  statusBadge: {
    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
  },
  warningBadge: {
    padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700,
    background: '#ffebee', color: '#c62828',
  },
  contactInfo: { fontSize: 14, color: '#666', margin: '4px 0 2px' },
  dateText: { fontSize: 13, color: '#999', margin: 0 },
  expandIcon: { fontSize: 14, color: '#999' },
  details: { padding: '0 20px 20px', borderTop: '1px solid #f0f0f0' },
  detailGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 20, marginTop: 16,
  },
  detailSection: { marginBottom: 8 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a2e', marginBottom: 10, borderBottom: '1px solid #f0f0f0', paddingBottom: 6 },
  detailRow: { display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 },
  detailLabel: { color: '#888', fontWeight: 500 },
  detailValue: { color: '#333', fontWeight: 600, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' },
  warnValue: { color: '#c62828' },
  redDot: {
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: '#c62828', marginLeft: 6, verticalAlign: 'middle',
  },
  bioSection: { marginTop: 16 },
  bioText: { fontSize: 14, color: '#555', lineHeight: 1.5 },
  staffRow: { fontSize: 13, color: '#555', margin: '4px 0' },
  actionRow: { display: 'flex', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #f0f0f0' },
  approveBtn: {
    padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: '#2e7d32', color: '#fff', fontWeight: 700, fontSize: 14,
  },
  rejectBtn: {
    padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: '#c62828', color: '#fff', fontWeight: 700, fontSize: 14,
  },
  pendingBtn: {
    padding: '10px 24px', borderRadius: 8, border: '1.5px solid #e0e0e0', cursor: 'pointer',
    background: '#fff', color: '#666', fontWeight: 700, fontSize: 14,
  },
};

export default PendingDealers;
